import React, { useState, useEffect, useCallback } from 'react';
import { Layout, Typography, Card, Button, Row, Col, Divider, message, Collapse, Modal, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import axios from 'axios';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { apiUrl } from './config';
import { useUser } from '../context/UserContext';
import { Preferences } from '@capacitor/preferences';


const { Content } = Layout;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// Una categoria contiene o direttamente delle schede, o delle sottocartelle
// che le raggruppano: il conteggio deve reggere entrambi i casi.
const contaSchede = (contenuto) => {
    if (Array.isArray(contenuto)) return contenuto.length;
    if (contenuto && typeof contenuto === 'object') {
        return Object.values(contenuto).reduce((somma, v) => somma + contaSchede(v), 0);
    }
    return 0;
};

const etichettaSchede = (n) => `${n} ${n === 1 ? 'scheda' : 'schede'}`;



const WorkoutPlansPage = () => {
    const [workoutPlans, setWorkoutPlans] = useState([]);
    const [isConfirmVisible, setIsConfirmVisible] = useState(false);
    const [isModifyConfirmVisible, setIsModifyConfirmVisible] = useState(false);
    const [isCopyConfirmVisible, setIsCopyConfirmVisible] = useState(false);
    const [newPlanName, setNewPlanName] = useState(''); // State for new plan name
    const [selectedPlan, setSelectedPlan] = useState(null);
    const navigate = useNavigate();
    const { user } = useUser(); // Prendi anche lo stato di loading dal contesto


    const fetchWorkoutPlans = useCallback(async () => {
        try {
            const token = (await Preferences.get({ key: 'token' })).value;
            const response = await axios.get(`${apiUrl}/api/trainer/schede`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                params: {
                    trainerId: user.trainerId,
                },
            });

            if (response.status !== 200) {
                throw new Error('Errore durante il recupero delle schede.');
            }
            setWorkoutPlans(response.data);
        } catch (error) {
            message.error('Errore durante il recupero delle schede.');
            console.error('Errore durante il recupero delle schede:', error);
        }
    }, [user.trainerId]); // Aggiungi user.trainerId come dipendenza

    useEffect(() => {
        fetchWorkoutPlans();
    }, [fetchWorkoutPlans]);


    const groupExercisesByDayAndMuscle = (exercises, cardioAbsExercises = []) => {
        const groupedByDay = exercises.reduce((days, exercise) => {
            const day = exercise.day || 'Altro';
            if (!days[day]) {
                days[day] = {};
            }
            const muscleGroup = exercise.muscleGroup || 'Altro';

            // Se il gruppo muscolare non esiste, crealo
            if (!days[day][muscleGroup]) {
                days[day][muscleGroup] = [];
            }

            days[day][muscleGroup].push(exercise);

            // Ordina gli esercizi all'interno di OGNI gruppo muscolare per numberExercise
            Object.keys(days[day]).forEach(group => {
                days[day][group].sort((a, b) =>
                    Number(a.numberExercise || 0) - Number(b.numberExercise || 0)
                );
            });

            return days;
        }, {});

        if (cardioAbsExercises.length > 0) {
            groupedByDay['Cardio/Addominali'] = {
                'Cardio/Addominali': cardioAbsExercises.sort((a, b) =>
                    Number(a.numberExercise || 0) - Number(b.numberExercise || 0)
                )
            };
        }

        return groupedByDay;
    };

    const confirmCopy = async () => {
        if (!selectedPlan || !newPlanName) {
            message.error("Nome scheda non valido.");
            return;
        }

        try {
            const token = (await Preferences.get({ key: 'token' })).value;
            await axios.post(`${apiUrl}/api/workoutPlans/copy`, {
                ...selectedPlan,
                workoutPlanName: newPlanName, // Use the new name for the copied plan
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            message.success("Scheda copiata con successo!");
            fetchWorkoutPlans(); // Refresh the list of workout plans
        } catch (error) {
            message.error("Errore durante la copia della scheda.");
            console.error("Errore durante la copia della scheda:", error);
        } finally {
            setIsCopyConfirmVisible(false); // Close the modal
        }
    };

    const showCopyConfirm = (plan) => {
        setSelectedPlan(plan);
        setNewPlanName(''); // Reset the new plan name
        setIsCopyConfirmVisible(true);
    };

    const confirmModify = () => {
        setIsModifyConfirmVisible(false);
        navigate(`/create-workout/${selectedPlan._id}`);
    };

    const generatePDF = async (plan) => {
        if (!plan || !plan.workoutPlanName) {
            message.error("Piano di allenamento non trovato.");
            return;
        }

        const doc = new jsPDF();
        const imgLogo = '/images/logo.png';

        // Aggiungi il logo e i dettagli del piano di allenamento
        doc.addImage(imgLogo, 'PNG', 10, 5, 50, 30);
        doc.setFontSize(16);
        doc.setFont('Helvetica', 'bold');
        doc.text(`Scheda: ${plan.workoutPlanName || 'Senza Nome'}`, 120, 20);

        if (plan.duration) {
            doc.setFontSize(12);
            doc.setFont('Helvetica', 'bold');
            doc.text(`Durata: ${plan.duration.value} ${plan.duration.unit}`, 10, 40);
        }

        if (plan.notes) {
            doc.setFontSize(12)
            doc.text(`Note Scheda: ${plan.notes}`, 10, 40)
        }

        let yOffset = 50;
        const groupedByDayAndMuscle = groupExercisesByDayAndMuscle(plan.exercises || [], plan.cardioAbsExercises || []);

        Object.keys(groupedByDayAndMuscle).forEach((day) => {
            const exercisesByMuscleGroup = groupedByDayAndMuscle[day];
            let exerciseNumber = 1; // Reset del contatore all'inizio di ogni giorno

            const validMuscleGroups = Object.keys(exercisesByMuscleGroup).filter((muscleGroup) =>
                exercisesByMuscleGroup[muscleGroup].some(exercise => (exercise.series && exercise.reps) || exercise.note)
            );

            if (validMuscleGroups.length > 0) {
                doc.setFont('Helvetica', 'bold');
                doc.setFontSize(12);
                doc.text(`Giorno ${day}`, 5, yOffset);
                yOffset += 5;

                validMuscleGroups.forEach((muscleGroup) => {
                    const exercises = exercisesByMuscleGroup[muscleGroup]
                        .filter(exercise => (exercise.series && exercise.reps) || exercise.note)
                        .sort((a, b) => a.numberExercise - b.numberExercise);

                    yOffset += 5;

                    if (exercises.length > 0) {
                        doc.setFont('Helvetica', 'bold');
                        doc.setFontSize(10);
                        doc.text(`Gruppo Muscolare: ${muscleGroup}`, 5, yOffset);
                        yOffset += 5;

                        exercises.forEach((exercise) => {
                            const imgData = exercise.image;
                            const imageHeight = 30;
                            const imageWidth = 30;

                            doc.setDrawColor(150);
                            doc.setLineWidth(0.1);
                            doc.line(10, yOffset, 200, yOffset);

                            yOffset += 2;

                            if (exercise.image) {
                                doc.addImage(imgData, 'JPEG', 10, yOffset, imageWidth, imageHeight);
                            }

                            const rowHeight = 6;
                            let currentYOffset = yOffset + 5;

                            doc.setFont('Helvetica', 'bold');
                            doc.setFontSize(9);

                            const exerciseNameLines = doc.splitTextToSize(`Esercizio ${exerciseNumber++}: ${exercise.name}`, 130);
                            doc.text(exerciseNameLines, 50, currentYOffset);
                            currentYOffset += rowHeight * exerciseNameLines.length;

                            doc.setFont('Helvetica', 'normal');

                            const seriesText = doc.splitTextToSize(`Serie: ${exercise.series}`, 130);
                            const repsText = doc.splitTextToSize(`Ripetizioni: ${exercise.reps}`, 130);
                            doc.text(seriesText, 50, currentYOffset);
                            doc.text(repsText, 100, currentYOffset);
                            currentYOffset += rowHeight;

                            if (exercise.rest) {
                                const restText = doc.splitTextToSize(`Riposo: ${exercise.rest} sec`, 130);
                                doc.text(restText, 50, currentYOffset);
                                currentYOffset += rowHeight * restText.length;
                            }

                            if (exercise.note) {
                                const noteText = doc.splitTextToSize(`Note: ${exercise.note}`, 130);
                                doc.text(noteText, 50, currentYOffset);
                                currentYOffset += rowHeight * noteText.length;
                            }

                            yOffset = currentYOffset + rowHeight + 10;

                            if (yOffset > 270) {
                                doc.addPage();
                                yOffset = 10;
                            }
                        });
                        yOffset += 5;
                    }
                });
                yOffset += 10;
            }
        });

        const pdfBlob = doc.output('blob');

        if (Capacitor.isNativePlatform()) {
            const reader = new FileReader();
            reader.readAsDataURL(pdfBlob);
            reader.onloadend = async () => {
                const base64Data = reader.result.split(',')[1];

                try {
                    const fileName = `FitLab-Scheda-${plan.workoutPlanName || 'SenzaNome'}.pdf`;

                    const writeFileResult = await Filesystem.writeFile({
                        path: `FitLab/${fileName}`,
                        data: base64Data,
                        directory: Directory.Documents,
                        recursive: true,
                    });

                    await Share.share({
                        title: 'Scarica il tuo PDF',
                        url: writeFileResult.uri,
                        dialogTitle: 'Condividi il piano di allenamento',
                    });
                } catch (e) {
                    console.error('Errore durante il salvataggio del file', e);
                }
            };
        } else {
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `FitLab-Scheda-${plan.workoutPlanName || 'SenzaNome'}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };




    const showModifyConfirm = (plan) => {
        setSelectedPlan(plan);
        setIsModifyConfirmVisible(true);
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`${apiUrl}/api/workoutPlans/${selectedPlan._id}`);
            message.success('Scheda cancellata con successo.');
            setIsConfirmVisible(false);
            fetchWorkoutPlans();
        } catch (error) {
            message.error('Errore durante la cancellazione della scheda.');
            console.error('Errore durante la cancellazione della scheda:', error);
        }
    };

    const showDeleteConfirm = (plan) => {
        setSelectedPlan(plan);
        setIsConfirmVisible(true);
    };

    const renderWorkoutPlansByFolder = (workoutPlans) => {
        const folders = {
            beginner: { men: [], woman: [] },
            intermediate: { men: [], woman: [] },
            advanced: { men: [], woman: [] },
            'workout of the day': [],
            'functional training': { men: [], woman: [] },
            'only fundamentals': [],
            'over 60': []
        };

        workoutPlans.forEach(plan => {
            if (Array.isArray(plan.folders)) {
                plan.folders.forEach(folder => {
                    const [folderKey, subFolderKey] = folder.split('/');
                    if (folders[folderKey]) {
                        if (['only fundamentals', 'over 60', 'workout of the day'].includes(folderKey)) {
                            folders[folderKey].push(plan);
                        } else if (folders[folderKey][subFolderKey]) {
                            folders[folderKey][subFolderKey].push(plan);
                        }
                    }
                });
            }
        });

        return Object.keys(folders).map((folderKey, index) => {
            const plansInFolder = folders[folderKey];

            return (
                <Collapse key={index} style={{ marginBottom: '1rem' }}>
                    <Panel
                        key={folderKey}
                        header={(
                            // Il numero di schede evita di dover aprire ogni
                            // categoria per sapere cosa contiene.
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                <span style={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                                    {folderKey.toUpperCase()}
                                </span>
                                <span style={{
                                    fontSize: 13,
                                    color: 'var(--testo-tenue)',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {etichettaSchede(contaSchede(plansInFolder))}
                                </span>
                            </span>
                        )}
                    >
                        {['only fundamentals', 'over 60', 'workout of the day'].includes(folderKey) ? (
                            <Row gutter={[16, 16]}>
                                {plansInFolder.map((plan, planIndex) => {
                                    const groupedByDayAndMuscle = groupExercisesByDayAndMuscle(plan.exercises, plan.cardioAbsExercises);

                                    return (
                                        <Col key={planIndex} xs={24} sm={12} lg={12}>
                                            <Card key={planIndex} style={{ marginBottom: '2rem', width: '100%' }} bordered>
                                                <Title level={4}>{plan.workoutPlanName || 'Nome Scheda'}</Title>
                                                <Divider style={{ marginBottom: '1rem' }} />

                                                <Collapse>
                                                    <Panel header="Mostra/Nascondi Esercizi" key={plan._id}>
                                                        {plan.duration && (
                                                            <Text style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--inchiostro)', display: 'block', marginBottom: '5px' }}>
                                                                Durata: {plan.duration.value} {plan.duration.unit}
                                                            </Text>
                                                        )}
                                                        {plan.notes && (
                                                            <Text style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--inchiostro)', display: 'block', marginBottom: '5px' }}>
                                                                Note Scheda: {plan.notes}
                                                            </Text>
                                                        )}
                                                        {Object.keys(groupedByDayAndMuscle).map((day, idx) => {
                                                            let exerciseCounter = 1; // Contatore per giorno
                                                            return (
                                                                <div key={idx}>
                                                                    <Title level={5} style={{ fontSize: '18px' }}>Giorno {day}</Title>
                                                                    {Object.keys(groupedByDayAndMuscle[day]).map((muscleGroup, muscleIdx) => (
                                                                        <div key={muscleIdx}>
                                                                            <Title level={6} style={{ fontSize: '16px' }}>{muscleGroup}</Title>
                                                                            <Row gutter={[16, 16]}>
                                                                                {groupedByDayAndMuscle[day][muscleGroup].map((exercise, exerciseIdx) => (
                                                                                    <React.Fragment key={exerciseIdx}>
                                                                                        <Col xs={24}>
                                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                                <img
                                                                                                    src={exercise.image}
                                                                                                    alt={exercise.name}
                                                                                                    style={{ height: '40px', width: '40px', marginRight: '10px', objectFit: 'cover' }}
                                                                                                />
                                                                                                <div>
                                                                                                    <Text strong>Esercizio {exerciseCounter}: </Text>
                                                                                                    <Text>{exercise.name}</Text><br />
                                                                                                    <Text strong>Serie: </Text>
                                                                                                    <Text>{exercise.series}</Text><br />
                                                                                                    <Text strong>Ripetizioni: </Text>
                                                                                                    <Text>{exercise.reps}</Text><br />
                                                                                                    {exercise.rest && (
                                                                                                        <>
                                                                                                            <Text strong>Riposo: </Text>{exercise.rest} sec<br />
                                                                                                        </>
                                                                                                    )}
                                                                                                    {exercise.note && (
                                                                                                        <>
                                                                                                            <Text strong>Note: </Text>{exercise.note}<br />
                                                                                                        </>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                            <Divider />
                                                                                        </Col>
                                                                                        {exerciseCounter++} {/* Incrementa il contatore per ogni esercizio */}
                                                                                    </React.Fragment>
                                                                                ))}
                                                                            </Row>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })}
                                                    </Panel>
                                                </Collapse>

                                                <Button type="default" onClick={() => generatePDF(plan)} style={{ marginTop: '1rem', width: '100%' }}>
                                                    Scarica PDF
                                                </Button>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                                                    <Button type="default" onClick={() => showCopyConfirm(plan)} style={{ flex: '0 1 auto', width: '30%', marginRight: '5px' }}>
                                                        Copia
                                                    </Button>
                                                    <Button type="primary" onClick={() => showModifyConfirm(plan)} style={{ flex: '0 1 auto', width: '30%', marginRight: '5px' }}>
                                                        Modifica
                                                    </Button>
                                                    <Button type="primary" danger onClick={() => showDeleteConfirm(plan)} style={{ flex: '0 1 auto', width: '30%' }}>
                                                        Cancella
                                                    </Button>
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        ) : (
                            Object.keys(plansInFolder).map((subFolderKey, subIndex) => {
                                if (plansInFolder[subFolderKey].length === 0) return null;
                                return (
                                    <Collapse key={subIndex} style={{ marginBottom: '1rem' }}>
                                        <Panel
                                            key={`${folderKey}-${subFolderKey}`}
                                            header={(
                                                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                                    <span style={{ fontWeight: 600 }}>{subFolderKey.toUpperCase()}</span>
                                                    <span style={{ fontSize: 13, color: 'var(--testo-tenue)', whiteSpace: 'nowrap' }}>
                                                        {etichettaSchede(contaSchede(plansInFolder[subFolderKey]))}
                                                    </span>
                                                </span>
                                            )}
                                        >
                                            <Row gutter={[16, 16]}>
                                                {plansInFolder[subFolderKey].map((plan, planIndex) => {
                                                    const groupedByDayAndMuscle = groupExercisesByDayAndMuscle(plan.exercises, plan.cardioAbsExercises);

                                                    return (
                                                        <Col key={planIndex} xs={24} sm={12} lg={12}>
                                                            <Card key={planIndex} style={{ marginBottom: '2rem', width: '100%' }} bordered>
                                                                <Title level={4}>{plan.workoutPlanName || 'Nome Scheda'}</Title>
                                                                <Divider style={{ marginBottom: '1rem' }} />

                                                                <Collapse>
                                                                    <Panel header="Mostra/Nascondi Esercizi" key={plan._id}>
                                                                        {plan.duration && (
                                                                            <Text style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--inchiostro)', display: 'block', marginBottom: '5px' }}>
                                                                                Durata: {plan.duration.value} {plan.duration.unit}
                                                                            </Text>
                                                                        )}
                                                                        {plan.notes && (
                                                                            <Text style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--inchiostro)', display: 'block', marginBottom: '5px' }}>
                                                                                Note Scheda: {plan.notes.value}
                                                                            </Text>
                                                                        )}
                                                                        {Object.keys(groupedByDayAndMuscle).map((day, idx) => {
                                                                            let exerciseCounter = 1; // Contatore per giorno
                                                                            return (
                                                                                <div key={idx}>
                                                                                    <Title level={5} style={{ fontSize: '18px' }}>Giorno {day}</Title>
                                                                                    {Object.keys(groupedByDayAndMuscle[day]).map((muscleGroup, muscleIdx) => (
                                                                                        <div key={muscleIdx}>
                                                                                            <Title level={6} style={{ fontSize: '16px' }}>{muscleGroup}</Title>
                                                                                            <Row gutter={[16, 16]}>
                                                                                                {groupedByDayAndMuscle[day][muscleGroup].map((exercise, exerciseIdx) => (
                                                                                                    <React.Fragment key={exerciseIdx}>
                                                                                                        <Col xs={24}>
                                                                                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                                                <img
                                                                                                                    src={exercise.image}
                                                                                                                    alt={exercise.name}
                                                                                                                    style={{ height: '40px', width: '40px', marginRight: '10px', objectFit: 'cover' }}
                                                                                                                />
                                                                                                                <div>
                                                                                                                    <Text strong>Esercizio {exerciseCounter}: </Text>
                                                                                                                    <Text>{exercise.name}</Text><br />
                                                                                                                    <Text strong>Serie: </Text>
                                                                                                                    <Text>{exercise.series}</Text><br />
                                                                                                                    <Text strong>Ripetizioni: </Text>
                                                                                                                    <Text>{exercise.reps}</Text><br />
                                                                                                                    {exercise.rest && (
                                                                                                                        <>
                                                                                                                            <Text strong>Riposo: </Text>{exercise.rest} sec<br />
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                    {exercise.note && (
                                                                                                                        <>
                                                                                                                            <Text strong>Note: </Text>{exercise.note}<br />
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                </div>
                                                                                                            </div>
                                                                                                            <Divider />
                                                                                                        </Col>
                                                                                                        {exerciseCounter++} {/* Incrementa il contatore per ogni esercizio */}
                                                                                                    </React.Fragment>
                                                                                                ))}
                                                                                            </Row>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </Panel>
                                                                </Collapse>

                                                                <Button type="default" onClick={() => generatePDF(plan)} style={{ marginTop: '1rem', width: '100%' }}>
                                                                    Scarica PDF
                                                                </Button>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
                                                                    <Button type="default" onClick={() => showCopyConfirm(plan)} style={{ flex: '0 1 auto', width: '30%', marginRight: '5px' }}>
                                                                        Copia
                                                                    </Button>
                                                                    <Button type="primary" onClick={() => showModifyConfirm(plan)} style={{ flex: '0 1 auto', width: '30%', marginRight: '5px' }}>
                                                                        Modifica
                                                                    </Button>
                                                                    <Button type="primary" danger onClick={() => showDeleteConfirm(plan)} style={{ flex: '0 1 auto', width: '30%' }}>
                                                                        Cancella
                                                                    </Button>
                                                                </div>
                                                            </Card>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </Panel>
                                    </Collapse>
                                );
                            })
                        )}
                    </Panel>
                </Collapse>
            );
        });
    };




    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Content style={{ maxWidth: '100%', margin: '0 10px', paddingBottom: '50px' }}>
                <div className="plan-title">
                    SCHEDE DI ALLENAMENTO
                </div>

                {workoutPlans.length > 0 ? (
                    renderWorkoutPlansByFolder(workoutPlans)
                ) : (
                    <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                        <div className='subtitle-schede'>
                            Nessuna scheda di allenamento disponibile.
                        </div>
                        <Button
                            type="primary"
                            onClick={() => navigate('/create-workout')}
                            style={{ marginTop: '20px' }}
                        >
                            Crea una nuova scheda
                        </Button>
                    </div>
                )}

                <Modal
                    title="Copia Scheda"
                    open={isCopyConfirmVisible}
                    onClose={() => setIsCopyConfirmVisible(false)}
                    getContainer={document.body}
                    footer={null}
                    onCancel={() => setIsCopyConfirmVisible(false)} // Ensure the modal closes on 'X'
                >
                    <div style={{ padding: '20px' }}>
                        <Typography variant="h6">Inserisci il nuovo nome per la scheda</Typography>
                        <Input
                            placeholder="Nome della nuova scheda"
                            value={newPlanName}
                            onChange={(e) => setNewPlanName(e.target.value)}
                            style={{ marginBottom: '10px' }}
                        />
                        <Button
                            onClick={() => setIsCopyConfirmVisible(false)}
                            style={{ marginRight: '10px' }}
                        >
                            Annulla
                        </Button>
                        <Button
                            type="primary"
                            onClick={confirmCopy}
                            style={{ marginRight: '10px' }} // Align with the cancel button
                        >
                            Conferma
                        </Button>
                    </div>
                </Modal>

                {/* Confirm Delete Modal */}
                <Modal
                    title="Conferma Cancellazione"
                    open={isConfirmVisible}
                    getContainer={document.body}
                    onOk={handleDelete}
                    onCancel={() => setIsConfirmVisible(false)}
                    okText="Conferma"
                    cancelText="Annulla"
                    className="custom-modal"
                >
                    <p>Sei sicuro di voler cancellare questa scheda?</p>
                </Modal>

                {/* Confirm Modify Modal */}
                <Modal
                    title="Conferma Modifica"
                    open={isModifyConfirmVisible}
                    getContainer={document.body}
                    onOk={confirmModify}
                    onCancel={() => setIsModifyConfirmVisible(false)}
                    okText="Conferma"
                    cancelText="Annulla"
                    className="custom-modal"
                />
            </Content>
        </Layout>
    );
};

export default WorkoutPlansPage;
